import React, { useRef, useState, useEffect } from 'react'
import { useUploadFiles } from './upload-files-context'
import { ImageFile } from '../image-file'
import ButtonAdminComponent, { ButtonStyle } from '../button-admin'

export function ImageUploader({ maxImages }: { maxImages?: number }) {
  const {
    files,
    urls,
    filesIds,
    addFile,
    addFiles,
    addUrl,
    removeFileIndex,
    removeUrlIndex,
    removeFileIdIndex,
  } = useUploadFiles()

  const [canAddMoreImages, setCanAddMoreImages] = useState(true)
  const [isDragging, setIsDragging] = useState(false)
  const [urlInput, setUrlInput] = useState('')

  useEffect(() => {
    setCanAddMoreImages(
      files.length + urls.length + filesIds.length < (maxImages || 1)
    )
  }, [maxImages, files, urls, filesIds])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])
    addFiles(selectedFiles)
    event.target.value = ''
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)

    const droppedFiles = Array.from(event.dataTransfer.files)
    addFiles(droppedFiles)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleRemoveImage = (index: number, isUrl: boolean) => {
    if (isUrl) {
      removeUrlIndex(index)
    } else {
      removeFileIndex(index)
    }
  }
  const handleRemoveUploadedImage = (index: number) => {
    removeFileIdIndex(index)
  }

  const handleAddUrl = () => {
    if (!urlInput.trim()) return

    try {
      new URL(urlInput)

      addUrl(urlInput)
      setUrlInput('')
    } catch (error) {
      console.error('Por favor, introduce una URL válida.')
    }
  }

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  return (
    <div className="w-full mb-8">
      <>
        <div
          onClick={(e) => {
            if (canAddMoreImages) {
              fileInputRef?.current?.click()
            }
            e.stopPropagation()
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
          className={`w-full ${canAddMoreImages ? 'cursor-pointer group' : 'cursor-not-allowed opacity-60'} border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-all duration-300 ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'bg-gray-50'
          }`}>
          <p className="text-gray-600">
            Arrastra y suelta imágenes aquí o{' '}
            <label
              className={`text-blue-500 font-medium ${canAddMoreImages ? 'cursor-pointer group-hover:underline' : 'cursor-not-allowed'} `}>
              selecciona archivos
            </label>
          </p>
          <input
            ref={fileInputRef}
            id="upload-files"
            type="file"
            accept="image/*"
            multiple
            disabled={!canAddMoreImages}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <div className="mt-6 flex flex-row gap-2">
          <input
            type="text"
            value={urlInput}
            disabled={!canAddMoreImages}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Ingresa una URL de imagen"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
          />
          <ButtonAdminComponent
            text={'Añadir'}
            onClick={handleAddUrl}
            loading={false}
            disabled={!canAddMoreImages}
            className={'w-fit'}
            style={ButtonStyle.PRIMARY}
          />
        </div>
      </>

      {(files.length > 0 || urls.length > 0 || filesIds.length > 0) && (
        <div className="mt-6 grid grid-cols-3 gap-4">
          {files.map((file, index) => {
            return (
              <div key={index} className="relative">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="w-full h-24 object-cover rounded-md"
                />
                <RemoveIcon onClick={() => handleRemoveImage(index, false)} />
              </div>
            )
          })}

          {urls.map((url, index) => (
            <div key={index} className="relative">
              <img
                src={url}
                alt="from-url"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/150' // Imagen de reemplazo en caso de error
                }}
                className="w-full h-24 object-cover rounded-md"
              />
              <RemoveIcon onClick={() => handleRemoveImage(index, true)} />
            </div>
          ))}

          {filesIds.map((fileId, index) => {
            return (
              <div key={index} className="relative">
                <ImageFile fileId={fileId} key={index} />
                <RemoveIcon onClick={() => handleRemoveUploadedImage(index)} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function RemoveIcon({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="absolute top-1 right-1 bg-red-500 text-white text-sm font-medium rounded-full w-6 h-6 flex items-center justify-center">
      ×
    </button>
  )
}
